import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import DateSelect from '../components/ui/DateSelect';
import { useStudentStore } from '../store/StudentStore';
import { useUser } from '../store/UserContext';
import { readFileAsStoredAttachment } from '../utils/fileAttachments';

const defaultCategories = ['UC', 'Medical', 'Social', 'Police', 'Other'];

const buildDoc = () => ({
  id: `doc-${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  file: null,
  fileUrl: '',
  tags: '',
  dueDate: '',
  expiryDate: '',
  notes: ''
});

export default function StudentDocumentCreatePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { controller } = useStudentStore();
  const { user } = useUser();
  const student = controller.getStudent(id);

  const [sections, setSections] = useState(() =>
    defaultCategories.map((category) => ({ category, documents: [] }))
  );
  const [newCategory, setNewCategory] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const existingCategories = useMemo(
    () => new Set(sections.map((section) => section.category.toLowerCase())),
    [sections]
  );

  const addSection = () => {
    const label = newCategory.trim();
    if (!label) return;
    if (existingCategories.has(label.toLowerCase())) {
      setFormError('That section already exists.');
      return;
    }
    setSections((prev) => [...prev, { category: label, documents: [] }]);
    setNewCategory('');
    setFormError('');
  };

  const addDocument = (category) => {
    setSections((prev) =>
      prev.map((section) =>
        section.category === category
          ? { ...section, documents: [...section.documents, buildDoc()] }
          : section
      )
    );
  };

  const removeDocument = (category, docId) => {
    setSections((prev) =>
      prev.map((section) =>
        section.category === category
          ? { ...section, documents: section.documents.filter((doc) => doc.id !== docId) }
          : section
      )
    );
  };

  const updateDocument = (category, docId, updates) => {
    setSections((prev) =>
      prev.map((section) =>
        section.category === category
          ? {
              ...section,
              documents: section.documents.map((doc) =>
                doc.id === docId ? { ...doc, ...updates } : doc
              )
            }
          : section
      )
    );
  };

  const MAX_FILE_SIZE = 20 * 1024 * 1024;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!student) return;
    if (saving) return;
    setSaving(true);
    setFormError('');

    const docsToAdd = [];

    for (const section of sections) {
      for (const doc of section.documents) {
        if (!doc.name.trim() && !doc.file) continue;
        if (doc.file && doc.file.size > MAX_FILE_SIZE) {
          setFormError('Each file must be 5MB or smaller. Please choose a smaller file.');
          return;
        }

        const name = doc.name.trim() || (doc.file ? doc.file.name.replace(/\.[^/.]+$/, '') : '');
        if (!name) continue;

        const additionalTags = doc.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean);
        const tags = [section.category, ...additionalTags];

        let uploadedFile = null;
        if (doc.file) {
          try {
            uploadedFile = await readFileAsStoredAttachment(doc.file, user?.name || 'System', {
              entityType: 'student',
              entityId: student.id,
              entityName: student.name,
              category: section.category,
              documentType: name
            });
          } catch (error) {
            setFormError(error?.message || 'Could not upload one of the files. Please try again.');
            return;
          }
        }

        const metadata = doc.file
          ? {
              type: doc.file.type || 'file',
              size: `${Math.round(doc.file.size / 1024)} KB`,
              fileName: doc.file.name,
              fileUrl: uploadedFile?.fileUrl || '',
              relativePath: uploadedFile?.relativePath || ''
            }
          : {};
        const versions = doc.file
          ? [
              {
                version: 1,
                uploadedBy: user?.name || 'System',
                uploadedAt: uploadedFile?.uploadedAt || new Date().toISOString(),
                fileName: doc.file.name,
                fileUrl: uploadedFile?.fileUrl || '',
                storageId: uploadedFile?.id || '',
                relativePath: uploadedFile?.relativePath || ''
              }
            ]
          : [];

        docsToAdd.push({
          name,
          tags,
          visibility: ['Admin', 'Manager', 'Staff'],
          metadata,
          dueDate: doc.dueDate || null,
          expiryDate: doc.expiryDate || null,
          notes: doc.notes?.trim() || '',
          versions
        });
      }
    }

    if (!docsToAdd.length) {
      setFormError('Add at least one document to upload.');
      setSaving(false);
      return;
    }

    try {
      docsToAdd.forEach((doc) => controller.addDocument(student.id, doc, user));
      navigate(`/students/${student.id}/profile?tab=documents`);
    } catch (error) {
      if (error?.message === 'STORAGE_QUOTA_EXCEEDED') {
        setFormError('Storage is full. Remove older documents or upload smaller files, then try again.');
      } else {
        setFormError('Unable to save documents. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!student) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Student not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/students')}>
          Back to students
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Upload Documents & Evidence</h2>
          <p className="sp-muted">Store UC, medical, social, police, and other evidence for this student.</p>
        </div>
        <Button variant="secondary" onClick={() => navigate(`/students/${student.id}/profile?tab=documents`)}>
          Back to documents
        </Button>
      </div>

      <Card className="p-6 space-y-5">
        {formError && <div className="sp-panel sp-panel--muted p-3 text-sm">{formError}</div>}

        <div className="sp-panel sp-panel--muted p-4 flex flex-wrap items-center gap-3">
          <div>
            <p className="sp-value" style={{ fontWeight: 600 }}>Add another section</p>
            <p className="text-xs sp-muted">Create custom categories beyond UC, Medical, Social, Police, and Other.</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              className="sp-input"
              value={newCategory}
              onChange={(event) => setNewCategory(event.target.value)}
              placeholder="e.g. Education"
            />
            <Button type="button" size="sm" variant="secondary" onClick={addSection}>
              Add section
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {sections.map((section) => (
            <div key={section.category} className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="sp-value" style={{ fontSize: '15px', fontWeight: 600 }}>
                    {section.category}
                  </h3>
                  <p className="text-xs sp-muted">Upload evidence for {section.category.toLowerCase()}.</p>
                </div>
                <Button type="button" size="sm" variant="secondary" onClick={() => addDocument(section.category)}>
                  Add document
                </Button>
              </div>

              {section.documents.length === 0 ? (
                <div className="text-sm sp-muted">No documents added in this section.</div>
              ) : (
                <div className="space-y-3">
                  {section.documents.map((doc) => (
                    <div key={doc.id} className="space-y-3">
                      <div className="grid md:grid-cols-4 gap-3 items-end">
                        <div>
                          <label className="sp-label">Document name</label>
                          <input
                            className="sp-input mt-2 w-full"
                            value={doc.name}
                            onChange={(event) =>
                              updateDocument(section.category, doc.id, { name: event.target.value })
                            }
                            placeholder="e.g. UC award letter"
                          />
                        </div>
                        <div>
                          <label className="sp-label">Upload</label>
                          <input
                            type="file"
                            className="sp-input mt-2 w-full"
                            onChange={(event) => {
                              const file = event.target.files?.[0] || null;
                              if (!file) {
                                updateDocument(section.category, doc.id, { file: null, fileUrl: '' });
                                return;
                              }
                              const reader = new FileReader();
                              reader.onload = () => {
                                updateDocument(section.category, doc.id, {
                                  file,
                                  fileUrl: reader.result?.toString() || ''
                                });
                              };
                              reader.readAsDataURL(file);
                            }}
                          />
                        </div>
                        <div>
                          <label className="sp-label">Due by (upload)</label>
                          <DateSelect
                            value={doc.dueDate}
                            onChange={(value) => updateDocument(section.category, doc.id, { dueDate: value })}
                            className="mt-2 w-full"
                          />
                        </div>
                        <div>
                          <label className="sp-label">Expiry date (optional)</label>
                          <DateSelect
                            value={doc.expiryDate}
                            onChange={(value) => updateDocument(section.category, doc.id, { expiryDate: value })}
                            className="mt-2 w-full"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="sp-label">Extra tags (optional)</label>
                        <div className="flex items-center gap-2 mt-2">
                          <input
                            className="sp-input w-full"
                            value={doc.tags}
                            onChange={(event) =>
                              updateDocument(section.category, doc.id, { tags: event.target.value })
                            }
                            placeholder="e.g. safeguarding, evidence"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => removeDocument(section.category, doc.id)}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                      <div>
                        <label className="sp-label">Notes (optional)</label>
                        <textarea
                          className="sp-textarea mt-2 w-full"
                          rows={2}
                          value={doc.notes}
                          onChange={(event) =>
                            updateDocument(section.category, doc.id, { notes: event.target.value })
                          }
                          placeholder="Add context for this document (e.g. where it was sourced)."
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate(`/students/${student.id}/profile?tab=documents`)}
            >
              Cancel
            </Button>
            <Button type="submit">Save documents</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
